import { useState, useMemo, useEffect } from "react";
import ReactECharts from "echarts-for-react";

import RotateDevice from "../../../components/RotateDevice/RotateDevice";
import "../../Admin/Ricavi/Ricavi.css";
import "../../HrGenerale/TrendKpi/TrendKpi.css";

// Grafico "Trend Costo Orario Medio HR" (vista Generale del report Power BI).
// Metrica = media per-riga AVG(costo/ore): non additiva, quindi si aggregano i
// componenti somma_costo_orario e cnt_ore_pos e si divide alla fine.

const nomiMesi = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const meseToTrimestre = { 1: 1, 2: 1, 3: 1, 4: 2, 5: 2, 6: 2, 7: 3, 8: 3, 9: 3, 10: 4, 11: 4, 12: 4 };

// €/h: sotto poche cifre bastano 1-2 decimali.
const fmtEuroOra = (v) => {
    if (v == null) return "";
    return `${v.toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} €/h`;
};

const FullscreenIcon = ({ active }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {active ? (
            <>
                <polyline points="4 14 10 14 10 20"></polyline>
                <polyline points="20 10 14 10 14 4"></polyline>
                <line x1="14" y1="10" x2="21" y2="3"></line>
                <line x1="3" y1="21" x2="10" y2="14"></line>
            </>
        ) : (
            <>
                <polyline points="15 3 21 3 21 9"></polyline>
                <polyline points="9 21 3 21 3 15"></polyline>
                <line x1="21" y1="3" x2="14" y2="10"></line>
                <line x1="3" y1="21" x2="10" y2="14"></line>
            </>
        )}
    </svg>
);

const ViewTypeToggle = ({ value, onChange }) => (
    <>
        <button type="button" className={`oper-hr-table-view-btn ${value === "mese" ? "active" : ""}`} onClick={() => onChange("mese")} title="Vista Mensile"><span>M</span></button>
        <button type="button" className={`oper-hr-table-view-btn ${value === "trimestre" ? "active" : ""}`} onClick={() => onChange("trimestre")} title="Vista Trimestrale"><span>T</span></button>
        <button type="button" className={`oper-hr-table-view-btn ${value === "anno" ? "active" : ""}`} onClick={() => onChange("anno")} title="Vista Annuale"><span>A</span></button>
    </>
);

const Generale = ({ rows }) => {
    const [viewType, setViewType] = useState("mese");
    const [isFullscreen, setIsFullscreen] = useState(false);

    useEffect(() => {
        const handleEsc = (e) => { if (e.key === "Escape") setIsFullscreen(false); };
        document.addEventListener("keydown", handleEsc);
        return () => document.removeEventListener("keydown", handleEsc);
    }, []);

    // Aggrega le righe del dataset per periodo (secondo il toggle), accumulando somma
    // dei rapporti e conteggio; l'euro/ora del bucket = Σsomma / Σcnt (null se cnt=0).
    const serie = useMemo(() => {
        const buckets = {};
        (rows || []).forEach(r => {
            const somma = Number(r.somma_costo_orario) || 0;
            const cnt = Number(r.cnt_ore_pos) || 0;
            let chiave, etichetta, ordine;
            if (viewType === "anno") {
                chiave = `${r.anno}`;
                etichetta = `${r.anno}`;
                ordine = r.anno * 100;
            } else if (viewType === "trimestre") {
                const t = meseToTrimestre[r.mese];
                chiave = `${r.anno}-T${t}`;
                etichetta = `T${t} ${r.anno}`;
                ordine = r.anno * 100 + t;
            } else {
                chiave = `${r.anno}-${String(r.mese).padStart(2, "0")}`;
                etichetta = `${nomiMesi[r.mese - 1]} ${r.anno}`;
                ordine = r.anno * 100 + r.mese;
            }
            if (!buckets[chiave]) buckets[chiave] = { etichetta, ordine, somma: 0, cnt: 0 };
            buckets[chiave].somma += somma;
            buckets[chiave].cnt += cnt;
        });
        const ordinati = Object.values(buckets).sort((a, b) => a.ordine - b.ordine);
        return {
            labels: ordinati.map(b => b.etichetta),
            values: ordinati.map(b => b.cnt > 0 ? Math.round((b.somma / b.cnt) * 100) / 100 : null),
        };
    }, [rows, viewType]);

    const options = useMemo(() => {
        const { labels, values } = serie;
        return {
            grid: { left: 55, right: 40, top: 40, bottom: 40, containLabel: false },
            tooltip: { trigger: "axis", formatter: (params) => `<strong>${params[0].name}</strong><br/>Costo Orario Medio: ${params[0].value != null ? fmtEuroOra(params[0].value) : "—"}` },
            xAxis: {
                type: "category",
                data: labels,
                axisLabel: { fontSize: 11, color: "#64748b" },
                axisLine: { lineStyle: { color: "#e2e8f0" } },
                axisTick: { show: false },
            },
            yAxis: { type: "value", axisLabel: { formatter: fmtEuroOra, fontSize: 11, color: "#64748b" }, splitLine: { lineStyle: { color: "#f1f5f9" } } },
            series: [{
                type: "line",
                data: values,
                smooth: false,
                connectNulls: true,
                symbol: "circle",
                symbolSize: 8,
                lineStyle: { color: "#2e6db1", width: 2 },
                itemStyle: { color: "#2e6db1", borderColor: "#fff", borderWidth: 2 },
                areaStyle: { color: { type: "linear", x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: "rgba(46, 109, 177, 0.35)" }, { offset: 1, color: "rgba(46, 109, 177, 0.02)" }] } },
                label: { show: true, position: "top", formatter: (p) => p.value != null ? fmtEuroOra(p.value) : "", fontSize: 10, color: "#334155", fontWeight: 600 },
            }],
        };
    }, [serie]);

    return (
        <div className={`admin-ricavi-section${isFullscreen ? " fullscreen-trend" : ""}`}>
            <RotateDevice active={isFullscreen} />
            <div className="admin-ricavi-section-header">
                <h3 className="admin-ricavi-section-title">Trend Costo Orario Medio HR</h3>
                <div className="oper-hr-table-view-type-container">
                    <ViewTypeToggle value={viewType} onChange={setViewType} />
                    <button className="trend-fullscreen-btn" onClick={() => setIsFullscreen(!isFullscreen)} title={isFullscreen ? "Esci da tutto schermo" : "Tutto schermo"}>
                        <FullscreenIcon active={isFullscreen} />
                    </button>
                </div>
            </div>
            <ReactECharts option={options} style={{ height: isFullscreen ? "calc(100vh - 120px)" : "300px", width: "100%" }} notMerge={true} />
        </div>
    );
};

export default Generale;
